import React from 'react';

const ANSVCLanding = () => {
  const imageCeo = '/images/image_ceo.jpg';
  const imageCto = '/images/image_cto.jpg';

  const styles = {
    body: {
      fontFamily: "-apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, Helvetica, Arial, sans-serif",
      lineHeight: '1.6',
      margin: 0,
      padding: 0,
      backgroundColor: '#121212',
      color: '#EAEAEA',
    },
    container: {
      maxWidth: '960px',
      margin: '0 auto',
      padding: '20px',
    },
    header: {
      textAlign: 'center',
      padding: '50px 0',
    },
    headerH1: {
      fontSize: '3.5rem',
      color: '#BB86FC',
      margin: 0,
    },
    headerP: {
      fontSize: '1.5rem',
      color: '#B3B3B3',
    },
    section: {
      padding: '40px 0',
      borderBottom: '1px solid #333',
    },
    sectionLast: {
      borderBottom: 'none',
    },
    h2: {
      fontSize: '2.5rem',
      color: '#03DAC6',
      textAlign: 'center',
      marginBottom: '40px',
    },
    grid: {
      display: 'grid',
      gridTemplateColumns: 'repeat(auto-fit, minmax(280px, 1fr))',
      gap: '30px',
    },
    card: {
      backgroundColor: '#1E1E1E',
      padding: '25px',
      borderRadius: '8px',
      border: '1px solid #333',
    },
    cardH3: {
      fontSize: '1.5rem',
      color: '#BB86FC',
      marginTop: 0,
    },
    cardP: {
      color: '#B3B3B3',
    },
    metric: {
      textAlign: 'center',
    },
    metricValue: {
      fontSize: '3rem',
      fontWeight: 'bold',
      color: '#03DAC6',
    },
    metricLabel: {
      fontSize: '1rem',
      color: '#B3B3B3',
    },
    teamMember: {
      textAlign: 'center',
    },
    teamMemberImg: {
      width: '100px',
      height: '100px',
      borderRadius: '50%',
      margin: '0 auto 15px',
    },
    teamMemberH4: {
      fontSize: '1.2rem',
      marginBottom: '5px',
    },
    teamMemberP: {
      fontSize: '0.9rem',
      color: '#B3B3B3',
      marginTop: 0,
    },
    ask: {
      backgroundColor: '#2a2a2a',
      padding: '30px',
      borderRadius: '8px',
      textAlign: 'center',
    },
    askP: {
        fontSize: '1.5rem',
        color: '#BB86FC',
        textAlign: 'center',
    },
    demoCard: {
      backgroundColor: '#2a2a2a',
      padding: '20px',
      borderRadius: '8px',
      textDecoration: 'none',
      color: 'inherit',
      display: 'block',
    },
    demoCardH4: {
      color: '#03DAC6',
    },
    interactive: {
      backgroundColor: '#D0B8FF',
      color: '#121212',
    },
    interactiveH4: {
        color: '#121212',
    }
  };

  return (
    <div style={styles.body}>
      <div style={styles.container}>
        <header style={styles.header}>
          <h1 style={styles.headerH1}>ANS: The Agent Network System</h1>
          <p style={styles.headerP}>The Universal Discovery & Trust Layer for the Internet of Agents</p>
        </header>

        <div style={styles.section}>
          <h2 style={styles.h2}>The Problem</h2>
          <p style={{ fontSize: '1.2rem', color: '#B3B3B3', textAlign: 'center', marginBottom: '20px' }}>AI agents are about to take over work - millions now, billions soon. They’ll negotiate, buy, decide, build.</p>
          <p style={{ fontSize: '1.2rem', color: '#B3B3B3', textAlign: 'center', marginBottom: '20px' }}>Yet today, they cannot even find each other, let alone trust each other.</p>
          <p style={{ fontSize: '1.2rem', color: '#B3B3B3', textAlign: 'center', marginBottom: '20px' }}>No discovery. No verification. No standards. No trust.</p>
          <p style={{ fontSize: '1.5rem', color: '#BB86FC', textAlign: 'center', fontWeight: 'bold' }}>Without trust, the agent economy cannot exist.</p>
        </div>

        <div style={styles.section}>
          <h2 style={styles.h2}>The Solution: A Hybrid Discovery Fabric</h2>
          <div style={styles.grid}>
            <div style={styles.card}>
              <h3 style={styles.cardH3}>High-Performance Global Directory</h3>
              <p style={styles.cardP}>ANS combines a scalable global directory with a decentralized trust layer, enabling agents to register once and be discovered by name or capability in under 50 milliseconds.</p>
            </div>
            <div style={styles.card}>
              <h3 style={styles.cardH3}>Progressive Trust Engine</h3>
              <p style={styles.cardP}>Our multi-tier verification model (digital signatures, third-party attestations, full blockchain consensus) provides flexible, context-specific security that balances performance and trust.</p>
            </div>
            <div style={styles.card}>
              <h3 style={styles.cardH3}>Cross-Organization Collaboration</h3>
              <p style={styles.cardP}>ANS facilitates seamless and secure collaboration across organizational boundaries, enabling enterprises to safely integrate and interact with external agents.</p>
            </div>
          </div>
        </div>

        <div style={styles.section}>
            <h2 style={styles.h2}>Live Demonstrations: ANS in Action</h2>
            <div style={styles.grid}>
                <div style={styles.demoCard}>
                    <h4 style={styles.demoCardH4}>RISK: The AI Wargame</h4>
                    <p>A decentralized wargame where six autonomous AI agents form alliances, negotiate, and compete for global domination, showcasing emergent strategy and complex coordination.</p>
                </div>
                <a href="https://mimic-dashboard-390011077376.us-central1.run.app/dashboard" target="_blank" rel="noopener noreferrer" style={{...styles.demoCard, ...styles.interactive}}>
                    <h4 style={styles.interactiveH4}>MIMIC: Market Intelligence</h4>
                    <p>A market forecasting platform where a swarm of 20+ unique AI "consumer" personas analyze visual concepts and predict market reception, providing actionable insights in minutes. Experience it live. Create an account and test it yourself.</p>
                </a>
                <div style={styles.demoCard}>
                    <h4 style={styles.demoCardH4}>A2A-ANS: AI Sales Team</h4>
                    <p>A practical demonstration of two AI sales agents (a junior assistant and a senior strategist) using ANS for discovery and A2A communication to develop a sales strategy in real-time.</p>
                </div>
                <div style={styles.demoCard}>
                    <h4 style={styles.demoCardH4}>Hang the DJ</h4>
                    <p>A multi-agent simulation inspired by Black Mirror, exploring emergent behavior and compatibility in simulated relationships between two autonomous agents over 100 dates.</p>
                </div>
                <a href="https://pod.gclouds.co.uk" target="_blank" rel="noopener noreferrer" style={{...styles.demoCard, ...styles.interactive}}>
                    <h4 style={styles.interactiveH4}>POD+: Talent Sourcing</h4>
                    <p>A multi-agent system where a Recruiter agent uses ANS to discover, screen, and shortlist top candidates from a pool of AI personas for a given Job Description.</p>
                </a>
                <div style={styles.demoCard}>
                    <h4 style={styles.demoCardH4}>POD+: Talent Intelligence Platform</h4>
                    <p>A B2B application where a multi-agent system continuously maps an organization's internal talent, enabling dynamic skill discovery, proactive retention, and strategic workforce planning.</p>
                </div>
            </div>
        </div>

        <div style={styles.section}>
            <h2 style={styles.h2}>Market & Traction</h2>
            <div style={styles.grid}>
                <div style={{...styles.card, ...styles.metric}}>
                    <div style={styles.metricValue}>60%+</div>
                    <div style={styles.metricLabel}>Software Market by 2030 (Goldman Sachs)</div>
                </div>
                <div style={{...styles.card, ...styles.metric}}>
                    <div style={styles.metricValue}>9B</div>
                    <div style={styles.metricLabel}>"Machine Customers" by 2028 (Gartner)</div>
                </div>
                <div style={{...styles.card, ...styles.metric}}>
                    <div style={styles.metricValue}>$1M - $3M</div>
                    <div style={styles.metricLabel}>ARR Early (Trust Registry)</div>
                </div>
            </div>
            <div style={{...styles.card, marginTop: '30px', textAlign: 'center'}}>
                <h3 style={styles.cardH3}>Early Design Partners</h3>
                <p style={styles.cardP}>Our design partner is Mitsubishi Electric Innovation Center, Mitsubishi US</p>
            </div>
        </div>

        <div style={styles.section}>
            <h2 style={styles.h2}>Business Model: The Revenue Stack</h2>
            <div style={styles.grid}>
                <div style={styles.card}>
                    <h3 style={styles.cardH3}>Enterprise Pilots</h3>
                    <p style={styles.cardP}>Engagements ranging from $100k - $500k each, providing tailored solutions and deep integration for large enterprises.</p>
                </div>
                <div style={styles.card}>
                    <h3 style={styles.cardH3}>SDK Subscriptions</h3>
                    <p style={styles.cardP}>Annual Recurring Revenue (ARR) of $50k - $200k per partner for access to our comprehensive SDKs and ongoing support.</p>
                </div>
                <div style={styles.card}>
                    <h3 style={styles.cardH3}>Trust Registry</h3>
                    <p style={styles.cardP}>Early stage Annual Recurring Revenue (ARR) projected at $1M - $3M from our core trust registry services.</p>
                </div>
            </div>
        </div>

        <div style={styles.section}>
            <h2 style={styles.h2}>The Ask</h2>
            <div style={styles.ask}>
                <p style={styles.askP}>Funding to take ANS from foundation (90% complete) &rarr; global standard.</p>
            </div>
        </div>

        <div style={styles.section}>
            <h2 style={styles.h2}>Founding Edge: An Experienced Team</h2>
            <div style={styles.grid}>
                <div style={{...styles.card, ...styles.teamMember}}>
                    <img src={imageCeo} alt="CEO" style={styles.teamMemberImg} />
                    <h4 style={styles.teamMemberH4}><a href="https://www.linkedin.com/in/tarikgorgun/" target="_blank"  rel="noopener noreferrer">CEO</a></h4>
                    <p style={styles.teamMemberP}>Led PE-backed companies in the U.S., Germany, and Japan through complex transformations with a focus on speed and execution.</p>
                </div>
                <div style={{...styles.card, ...styles.teamMember}}>
                    <img src={imageCto} alt="CTO" style={styles.teamMemberImg} />
                    <h4 style={styles.teamMemberH4}><a href="https://www.linkedin.com/in/gulercan/" target="_blank"  rel="noopener noreferrer">CTO</a></h4>
                    <p style={styles.teamMemberP}>Founder of gClouds (Google Cloud Partner) and gLabs. Expert in building autonomous AI agent networks and the underlying communication and discovery protocols (A2A, ANS).</p>
                </div>
            </div>
             <div style={{...styles.card, marginTop: '30px', textAlign: 'center'}}>
                <h3 style={styles.cardH3}>World-Class Advisors</h3>
                <p style={styles.cardP}>Our advisory board includes the Mitsubishi Electric Innovation Centre.</p>
            </div>
        </div>

        <div style={{...styles.section, ...styles.sectionLast}}>
            <h2 style={styles.h2}>Our Vision: The Internet of AI Agents</h2>
            <p style={{textAlign: 'center', fontSize: '1.2rem'}}>Our vision is the Internet of AI Agents; the next internet. By embedding ANS into cloud SDKs, device firmware, and regulation frameworks, we will create defensible network effects and become the backbone of the new agent economy.</p>
        </div>

        {/* QR Code and Thumbnail Section */}
        <div style={{
          textAlign: 'center',
          padding: '40px 0',
          display: 'flex',
          justifyContent: 'center',
          alignItems: 'center',
          flexWrap: 'wrap',
          gap: '40px'
        }}>
          <div style={{textAlign: 'center'}}>
            <p style={{...styles.cardP, marginBottom: '10px'}}>Scan to view on your mobile</p>
            <img src="/images/qr-ans-vc.png" alt="QR Code for ANS VC Landing Page" style={{width: '150px', height: '150px', margin: '0 auto'}} />
          </div>
          <div style={{textAlign: 'center'}}>
            <p style={{...styles.cardP, marginBottom: '10px'}}>Explore the full ANS research page</p>
            <a href="/ans" style={{textDecoration: 'none'}}>
              <img src="/images/TheNexus1.png" alt="ANS Research Page Thumbnail" style={{width: '150px', height: '150px', margin: '0 auto', borderRadius: '8px', border: '1px solid #333', objectFit: 'cover'}} />
            </a>
          </div>
        </div>

      </div>
    </div>
  );
};

export default ANSVCLanding;